<template>
  <div class="student-menu-wrap" ref="wrapRef">
    <!-- Loading skeleton -->
    <div v-if="!initialized" class="student-menu-skeleton" aria-hidden="true" />

    <!-- Not logged in: show the login entry. Visibility of the whole menu is gated
         upstream (header renders it only in devMode), so no authEnabled check here —
         otherwise a non-logged-in user fell through to the avatar branch and saw "?". -->
    <template v-else-if="!isLoggedIn">
      <!-- icon-only for header -->
      <button v-if="loginVariant === 'icon'" class="student-login-icon-btn" type="button" :title="t('layout.header.actions.loginButton', 'Увійти')" @click="handleLogin()">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <circle cx="12" cy="8" r="4"/>
          <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
        </svg>
      </button>
      <!-- nav-link style for sidebar nav list -->
      <button v-else-if="loginVariant === 'nav'" class="student-login-nav-btn" type="button" @click="handleLogin()">
        {{ t('layout.header.actions.loginButton', 'Увійти') }}
      </button>
      <!-- outlined text button -->
      <button v-else class="student-login-outline-btn" type="button" @click="handleLogin()">
        {{ t('layout.header.actions.loginButton', 'Увійти') }}
      </button>
    </template>

    <!-- Logged in: Avatar trigger -->
    <button v-else ref="avatarBtnRef" class="student-avatar-btn" @click="open = !open" :aria-expanded="open">
      <span class="student-avatar">{{ initials }}</span>
    </button>

    <!-- Dropdown menu — teleported to body so it floats above offcanvas/other containers -->
    <Teleport to="body">
      <Transition name="student-menu">
        <div v-if="open && isLoggedIn" class="student-menu" :style="dropdownStyle" role="menu">
          <div class="student-menu__header">
            <span class="student-avatar student-avatar--lg">{{ initials }}</span>
            <div class="student-menu__info">
              <span class="student-menu__name">{{ displayName }}</span>
              <span v-if="profile?.contact_email" class="student-menu__email">{{ profile.contact_email }}</span>
            </div>
          </div>
          <div class="student-menu__divider" />
          <NuxtLink class="student-menu__item" :to="progressPath" @click="open = false">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01"/></svg>
            {{ t('layout.header.student.progress', 'Мій прогрес') }}
          </NuxtLink>
          <NuxtLink class="student-menu__item" :to="profilePath" @click="open = false">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
            {{ t('layout.header.student.profile', 'Профіль') }}
          </NuxtLink>
          <div class="student-menu__divider" />
          <button class="student-menu__item student-menu__item--danger" @click="handleLogout">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
            {{ t('layout.header.student.logout', 'Вийти') }}
          </button>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { useStudentStore } from "@/stores/student"
import { useStudentLoginModal } from "@/composables/study/useStudentLoginModal"

const { t, locale } = useI18n()
const router = useRouter()
const store = useStudentStore()
const { isLoggedIn, displayName, initials, profile, loading, initialized } = storeToRefs(store)

defineProps<{ loginVariant?: 'icon' | 'text' | 'nav' }>()
const emit = defineEmits<{ close: [] }>()

const open = ref(false)
const { openLoginModal } = useStudentLoginModal()
const avatarBtnRef = ref<HTMLElement | null>(null)

function handleLogin() {
  emit('close')
  openLoginModal()
}
const wrapRef = ref<HTMLElement | null>(null)
onMounted(() => {
  const handler = (e: MouseEvent) => { if (wrapRef.value && !wrapRef.value.contains(e.target as Node)) open.value = false }
  document.addEventListener("mousedown", handler)
  onUnmounted(() => document.removeEventListener("mousedown", handler))
})

const dropdownStyle = computed(() => {
  if (!avatarBtnRef.value) return {}
  const rect = avatarBtnRef.value.getBoundingClientRect()
  return {
    position: 'fixed' as const,
    top: `${rect.bottom + 8}px`,
    right: `${window.innerWidth - rect.right}px`,
    zIndex: 9999,
  }
})

const progressPath = computed(() => `/my/progress`)
const profilePath = computed(() => `/my/profile`)
const loginUrl = computed(() => {
  const redirect = router.currentRoute.value.fullPath
  return `/my/login?redirect=${encodeURIComponent(redirect)}`
})

async function handleLogout() {
  open.value = false
  await store.logout()
  router.push('/')
}

</script>

<style scoped>
.student-menu-wrap { position: relative; }

.student-menu-skeleton {
  width: 72px; height: 32px; border-radius: 8px;
  background: linear-gradient(90deg, #e8e8e8 25%, #f4f4f4 50%, #e8e8e8 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.4s infinite;
}
@keyframes skeleton-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.student-login-icon-btn {
  width: 36px; height: 36px; border-radius: 50%;
  background: none; border: 1.5px solid var(--button-passive-border, #D2EBF0);
  cursor: pointer; display: flex; align-items: center; justify-content: center;
  color: var(--color-dark-blue); transition: border-color 0.15s, background 0.15s;
}
.student-login-icon-btn:hover {
  border-color: var(--gradient-primary-start, #09AFEA);
  background: var(--gradient-secondary-start, #D2EBF0);
}

.student-login-nav-btn {
  font-family: var(--font-inter); font-weight: 500; font-size: 18px;
  color: #252840; text-decoration: none; padding: 12px 0;
  background: none; border: none; cursor: pointer;
  width: 100%; text-align: left; display: block;
  transition: color 0.2s ease;
}
.student-login-nav-btn:hover { color: #00CE9B; }

.student-login-outline-btn {
  background: none; border: 1.5px solid var(--button-passive-border, #D2EBF0);
  border-radius: 8px; padding: 7px 18px; cursor: pointer;
  font-family: var(--font-inter); font-size: 14px; font-weight: 600;
  color: var(--color-dark-blue); transition: border-color 0.15s, background 0.15s;
  white-space: nowrap;
}
.student-login-outline-btn:hover {
  border-color: var(--gradient-primary-start, #09AFEA);
  background: var(--gradient-secondary-start, #D2EBF0);
}

.student-avatar-btn {
  background: none; border: none; cursor: pointer; padding: 0;
  display: flex; align-items: center;
}
.student-avatar {
  width: 36px; height: 36px; border-radius: 50%;
  background: linear-gradient(135deg, var(--gradient-primary-start, #09AFEA), var(--gradient-primary-end, #19E089));
  color: #fff; font-size: 13px; font-weight: 300;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0; letter-spacing: 0.5px;
}
.student-avatar--lg { width: 44px; height: 44px; font-size: 16px; }

.student-menu {
  background: #fff; border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.14);
  min-width: 220px; z-index: 500; overflow: hidden;
  border: 1px solid rgba(0,0,0,0.06);
}
.student-menu__header {
  display: flex; align-items: center; gap: 10px; padding: 14px 16px;
}
.student-menu__info { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.student-menu__name { font-size: 14px; font-weight: 600; color: var(--color-dark-blue); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.student-menu__email { font-size: 12px; color: var(--color-grey); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.student-menu__divider { height: 1px; background: rgba(0,0,0,0.06); margin: 4px 0; }
.student-menu__item {
  display: flex; align-items: center; gap: 10px;
  padding: 11px 16px; font-size: 14px; font-weight: 500;
  color: var(--color-dark-blue); text-decoration: none; cursor: pointer;
  background: none; border: none; width: 100%; text-align: left;
  transition: background 0.12s;
}
.student-menu__item:hover { background: rgba(0,0,0,0.04); }
.student-menu__item--danger { color: rgb(var(--v-theme-error, 220 38 38)); }

.student-menu-enter-active, .student-menu-leave-active { transition: opacity 0.14s, transform 0.14s; }
.student-menu-enter-from, .student-menu-leave-to { opacity: 0; transform: translateY(-6px); }
</style>
